<script lang="ts">
    import { getContext, onDestroy, tick } from 'svelte'
    import { get, derived as sderived, writable } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { cloneDeep, isEqual, orderBy } from 'lodash-es'
    import { DateTime } from 'luxon'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { asAny, sanchor, sgoto, spath } from 'hydria/svelte_helpers'
    import { makeSortString } from 'hydria/utils/strings'

    import { makeCompareContact, sortContacts, tldRules as tldRulesStore } from 'wdom/services/contacts'
    import type { IAddressGroup, IContact, ITldRules } from 'wdom/services/contacts'
    import { Breadcrumb } from 'breadcrumb'

    import type { GenOrder, IItem, IOrder } from './order'

    import ContactGroupEditor from 'wdom/components/contacts/ContactGroupEditor.svelte'

    const orderId = getContext('orderId') as Readable<string>
    const Order = getContext('Order') as GenOrder

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface IItemInOrder {
        a_name: string
        u_name: string
        tld: string
        sortName: string
        validityCode: string
        isValid: boolean
    }

    // const ready = sderived(Order.ready, ($ready) => $ready)
    let ready = $state(false)

    async function loadStuff() {
        let loads = [loadAddresses(), loadUserInfos(), loadShortDomainList(), loadContactsFromOrder()]
        for (const l of loads) {
            await l
        }
        contactStatus = 'ready'
    }
    unsubs.push(
        Order.ready.subscribe(($ready) => {
            ready = $ready
            if ($ready) {
                tick().then(() => {
                    loadStuff()
                })
            }
        }),
    )

    let tldRules: ITldRules = $state(get(tldRulesStore))
    unsubs.push(
        tldRulesStore.subscribe((v) => {
            tldRules = v
        }),
    )

    // const status = sderived(Order.status, ($status) => $status)
    let status: 'not-found' | 'ready' | 'loading' | 'error' = $state('loading')
    unsubs.push(
        Order.status.subscribe((v) => {
            status = v
        }),
    )
    // const items = sderived(Order.items, ($items) => $items)
    let items: IItem[] = $state([])
    unsubs.push(
        Order.items.subscribe((v) => {
            items = v
        }),
    )

    // const order = sderived(Order.orderStore, ($order) => $order)
    let order: IOrder = $state(get(Order.orderStore))
    unsubs.push(
        Order.orderStore.subscribe((v) => {
            order = v
        }),
    )

    const crumbs = sderived([Order.status, Order.orderStore], ([$status, $order]) => {
        const crs: CrumbArray = [{ txt: tgettext('Orders'), url: spath('/ecom/orders') }]

        if ($status == 'ready' || $status == 'loading') {
            if ($status == 'ready' && $order)
                crs.push({
                    txt: $order.ctime.toLocaleString(DateTime.DATETIME_MED),
                })
            else if ($status == 'ready' || $status == 'loading')
                crs.push({
                    txt: '<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />',
                    html: true,
                })
            crs.push({
                txt: `<span class="text-muted">${Order.orderId}</span>`,
                html: true,
            })
        } else {
            crs.push({
                txt: iconExclamationTriangle,
                html: true,
            })
        }

        return crs
    })

    let inlineModal: boolean = $state(true)

    let addresses: IAddressGroup[] = $state([])
    let loadedAddressesUserId: string
    let addressesReady = $state(false)
    async function loadAddresses() {
        if (order.owner == loadedAddressesUserId) return

        try {
            addresses = await Order.loadAddresses()
        } catch {
            // Just be silent for now
            addresses = []
        }
        loadedAddressesUserId = order.owner
        addressesReady = true
    }

    let contactStatus: 'loading' | 'saving' | 'ready' = $state('loading')

    let canEditRegistrant = true
    let editionAllowed = true

    // These are the contacts we send to the editor as the "currently saved".
    let contacts: IContact[] = $state([])
    let originalContacts: IContact[] = $state([])

    const tldValidity: Writable<{ [key: string]: boolean }> = writable({})

    // This acts as a signal to the group editor: set the edited value to this
    let setContacts: IContact[] = $state([])

    // This is what the editor updates when there are changes
    const contactsEdited: Writable<IContact[]> = writable([])

    function resetToSaved() {
        originalContacts = sortContacts(order.meta.contacts)
        setContacts = cloneDeep(originalContacts)
    }

    function loadContactsFromOrder() {
        originalContacts = sortContacts(order.meta.contacts)
        contacts = cloneDeep(originalContacts)
    }

    // ##############################################################  Load Short Domain List
    interface IShortDomain {
        idna: string
        name: string
    }
    let shortDomainList: IShortDomain[] = $state([])
    async function loadShortDomainList() {
        try {
            const r = await simpleFetch('GET', `/json/ecom/1/user/${order.owner}/contact-domain-list`)
            shortDomainList = r.data.domains
        } catch {}
    }

    let preferedLg: string | undefined = $state()
    async function loadUserInfos() {
        try {
            const r = await simpleFetch('GET', `/json/wdom-user/1/${order.owner}/name`)
            preferedLg = r.data.prefered_lg
        } catch {}
    }

    let shortDomainSelectionTypes = $state({
        registrant: true,
        admin: true,
        billing: true,
        tech: true,
    })
    const contactTexts = {
        registrant: 'Titulaire',
        admin: 'Administratif',
        billing: 'Facturation',
        tech: 'Technique(s)',
    }

    let showShortDomainSelection = $state(false)
    async function importShortDomain(sdomain: IShortDomain) {
        showShortDomainSelection = false

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/domain/1/${sdomain.idna}`, { with_contacts: '1' })
        } catch (e) {
            notify({
                type: 'danger',
                title: $_('Contacts'),
                text: $_('Failed to load contacts from “{domain}”', { domain: sdomain.name }),
            })
            return
        }

        let lcontacts: IContact[] = []

        for (const c of r.data.contacts) {
            if (c.type == 'owner') c.type = 'registrant'
        }
        for (const [ctype, isSel] of Object.entries(shortDomainSelectionTypes)) {
            const eqType = ['registrant', 'owner'].includes(ctype) ? ['registrant', 'owner'] : [ctype]
            if (isSel && (ctype != 'registrant' || canEditRegistrant)) {
                // Import that contact...
                lcontacts = [...lcontacts, ...r.data.contacts.filter((lc) => eqType.includes(lc.type))]
            } else {
                // Keep the original contact
                lcontacts = [...lcontacts, ...cloneDeep(contacts.filter((lc) => eqType.includes(lc.type)))]
            }
        }
        setContacts = sortContacts(lcontacts)
    }

    // ##############################################################  Save
    async function save() {
        contactStatus = 'saving'

        let r: SimpleFetchResult

        try {
            r = await simpleFetch('PUT', `/json/ecom/1/order/${$orderId}/contacts`, { contacts: $contactsEdited })
        } catch (e) {
            console.error('Failed to set contacts:', e)
            notify({
                type: 'danger',
                title: tgettext('Order / Contacts'),
                text: 'Les contacts n’ont pas été sauvegardés.',
            })
            contactStatus = 'ready'
            return
        }

        Order.load()
        notify({
            type: 'success',
            title: tgettext('Order / Contacts'),
            text: 'Les contacts ont été modifiés.',
        })
        sgoto(`/ecom/order/${$orderId}`)
    }

    let domainUnamesInOrder: string[] = $derived(items && tldRules ? items.filter((item) => item.type == 'DOMAIN').map((item) => item.meta.u_name) : [])
    let domainsInOrderData: IItemInOrder[] = $derived(
        items && tldRules
            ? orderBy(
                  items
                      .filter((item) => item.type == 'DOMAIN')
                      .map((item) => {
                          const tld = item.meta.u_name.split('.').pop()
                          const validityCode = tld == 'ca' ? 'cira' : tldRules[tld] != null ? tld : 'general'
                          const data = {
                              a_name: item.meta.a_name,
                              u_name: item.meta.u_name,
                              tld: tld,
                              // sortName: makeSortString(`${tld} : ${item.meta.u_name}`),
                              sortName: makeSortString(item.meta.u_name),
                              validityCode: validityCode,
                              isValid: !!$tldValidity[validityCode],
                          }
                          return data
                      }),
                  [
                      (item) => {
                          return item.sortName
                      },
                  ],
              )
            : [],
    )

    let allValid = $derived.by(() => {
        let localAllValid = true
        for (const item of domainsInOrderData) {
            localAllValid &&= item.isValid
        }
        return localAllValid
    })

    let originalContactsForComparison: IContact[] | null = $derived(originalContacts == null ? null : originalContacts.map((c) => makeCompareContact(c, preferedLg)))

    let contactsEditedForComparison = $derived($contactsEdited == null ? null : $contactsEdited.map((c) => makeCompareContact(c, preferedLg)))
    let dirty = $derived(originalContactsForComparison != null && contactsEditedForComparison != null && !isEqual(contactsEditedForComparison, originalContactsForComparison))
    // ##############################################################  Short domains

    $effect(() => {
        if (showShortDomainSelection && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!showShortDomainSelection && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<Breadcrumb {crumbs} />

{#if contactStatus == 'loading'}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-header text-bg-primary">{$_('Domaines dans la commande')}</div>
        <div class="order-contact-edit-list">
            <table class="table mb-0">
                <thead>
                    <tr>
                        <th class="text-center" style="width: 14em;">{$_('Contacts valid')}</th>
                        <th>{$_('Domain')}</th>
                    </tr>
                </thead>
                <tbody class="tbody-list-item">
                    {#each domainsInOrderData as domain}
                        <tr>
                            <td class="text-center">
                                {#if asAny(domain).isValid}
                                    <span class="text-success">
                                        <Icon inline icon="fa:check" />
                                    </span>
                                {/if}
                            </td>
                            <td>{asAny(domain).u_name}</td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    </div>

    <div id="container-domain" class="mt-3" class:has-contact-panel={inlineModal && editionAllowed}>
        <div class="card card-contacts">
            <div class="card-body">
                <div class="card-title contact-title">
                    <div class="contacts-title">{$_('Contacts')}</div>
                    <div class="actions domain-contact-actions">
                        <div class="btn-group btn-group-sm">
                            <a use:sanchor href={spath(`/ecom/order/${$orderId}`)} class="btn btn-outline-secondary"><Icon inline icon="fa:arrow-circle-left" />&nbsp;&nbsp;<span class="hidden-xxs hidden-cpan-cond-md">{$_('Return')}</span></a>
                        </div>
                        {#if editionAllowed}
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn btn-outline-secondary" class:active={!inlineModal} onclick={() => (inlineModal = !inlineModal)}><Icon inline icon="fa:external-link" /></button>
                            </div>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" onclick={() => (showShortDomainSelection = true)} disabled={contactStatus == 'saving' || !shortDomainList || !shortDomainList.length}>
                                    <Icon inline icon="fa:globe" /><span class="hidden-xs hidden-sm hidden-cpan-cond-md">&nbsp;&nbsp;{$_('Import from another domain')}</span>
                                </button>
                            </div>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" onclick={() => resetToSaved()} disabled={contactStatus == 'saving' || !dirty}>
                                    <Icon inline icon="fa:undo" /><span class="hidden-xs hidden-cpan-cond-md">&nbsp;&nbsp;{$_('Undo changes')}</span>
                                </button>
                                <button class="btn btn-primary" type="button" onclick={() => save()} disabled={contactStatus == 'saving' || !dirty || !allValid}>
                                    <Icon inline icon="fa:floppy-o" /><span class="hidden-xxs hidden-cpan-cond-md">&nbsp;&nbsp;{$_('Save')}</span>
                                </button>
                            </div>
                        {/if}<!-- edition allowed -->
                    </div>
                </div>

                <ContactGroupEditor context="multiple" {contacts} bind:setContacts {contactsEdited} updating={contactStatus != 'ready'} domains={domainUnamesInOrder} {canEditRegistrant} {tldValidity} {addresses} inlineMode={inlineModal} lg={preferedLg} />
            </div>
        </div>
    </div>
{/if}

{#if showShortDomainSelection}
    <div class="modal modal-contact-alt-domain" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Charger les contacts d’un autre domaine')}</div>
                </div>
                <div class="modal-body">
                    <div class="alt-domain-contact-selection">
                        {#each shortDomainList as sdomain}
                            <div>
                                <a
                                    href="/"
                                    onclick={(e) => {
                                        e.preventDefault()
                                        importShortDomain(sdomain)
                                    }}>{sdomain.name}</a
                                >
                            </div>
                        {/each}
                    </div>
                </div>
                <div class="modal-footer">
                    {#each Object.entries(shortDomainSelectionTypes) as [ttype, tval]}
                        <label for="short-c-{ttype}" style="white-space: nowrap; margin-right: 0.5em">
                            <input type="checkbox" id="short-c-{ttype}" bind:checked={shortDomainSelectionTypes[ttype]} />
                            {contactTexts[ttype]}
                        </label>
                    {/each}
                    <button type="button" class="btn btn-outline-secondary" onclick={() => (showShortDomainSelection = false)}>{$_('Cancel')}</button>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" onclick={() => (showShortDomainSelection = false)}>{$_('Cancel')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" aria-hidden="true" onclick={() => (showShortDomainSelection = false)}></div>
    </div>
{/if}
